import { useCallback, useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { fmt } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Check, RefreshCw, X } from "lucide-react";

const STATUS_BADGE = {
  pendiente: "bg-amber-100 text-amber-700",
  aprobada: "bg-emerald-100 text-emerald-700",
  rechazada: "bg-rose-100 text-rose-700",
  bloqueada: "bg-rose-100 text-rose-700",
  no_requerido: "bg-slate-100 text-slate-600",
};

const TYPE_LABEL = {
  monto: "Cambiar monto USDT",
  precio: "Cambiar precio/tasa",
  porcentaje: "Cambiar %",
  total: "Cambiar total",
  moneda: "Cambiar moneda",
  memo: "Cambiar memo",
  fecha: "Cambiar fecha y hora",
  anular: "Anular transacción",
  otro: "Otro",
};

export default function Correcciones({ embedded = false }) {
  const { isAdmin, isOperator } = useAuth();
  const [list, setList] = useState([]);
  const [closures, setClosures] = useState([]);
  const [reviewing, setReviewing] = useState(null);
  const [reviewingClosure, setReviewingClosure] = useState(null);
  const [comment, setComment] = useState("");

  const load = useCallback(async () => {
    const [correctionsResponse, closuresResponse] = await Promise.all([
      api.get("/corrections"),
      isAdmin ? api.get("/month-closures") : Promise.resolve({ data: [] }),
    ]);
    setList(Array.isArray(correctionsResponse.data) ? correctionsResponse.data : []);
    setClosures(Array.isArray(closuresResponse.data) ? closuresResponse.data : []);
  }, [isAdmin]);
  useEffect(() => { load(); }, [load]);

  const review = async (action) => {
    try {
      await api.post(`/corrections/${reviewing.id}/${action}`, { admin_comment: comment });
      toast.success(action === "approve" ? "Aprobada" : "Rechazada");
      setReviewing(null); setComment(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const reviewClosure = async (action) => {
    try {
      await api.post(`/month-closures/${reviewingClosure.id}/${action}`, { admin_comment: comment });
      toast.success(action === "approve" ? "Cierre aprobado y aplicado" : "Cierre rechazado");
      setReviewingClosure(null); setComment(""); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const regenerateClosure = async (closure) => {
    if ((closure.status === "aprobada" || closure.status === "no_requerido") && !window.confirm("Esto recalculara este cierre y los cierres aprobados posteriores del mismo mercado. ¿Continuar?")) {
      return;
    }
    try {
      await api.post(`/month-closures/${closure.id}/regenerate`, { admin_comment: "" });
      toast.success(closure.status === "aprobada" || closure.status === "no_requerido" ? "Revision de cierre recalculada" : "Propuesta de cierre recalculada");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const renderProposed = (corr) => {
    if (corr.request_type === "anular") return <span className="text-rose-600">Anular transacción</span>;
    const entries = Object.entries(corr.proposed_data || {}).filter(([k, v]) => v !== null && v !== "" && v !== undefined);
    if (entries.length === 0) return "—";
    return entries.map(([k, v]) => `${k}: ${v}`).join(", ");
  };

  return (
    <div className="space-y-6" data-testid="correcciones-page">
      {!embedded && (
        <div>
          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 tracking-tight">Solicitudes de Corrección</h1>
          <p className="text-slate-500 mt-1">
            {isAdmin && "Revisa y aprueba/rechaza solicitudes de operadores."}
            {isOperator && "Mis solicitudes enviadas al administrador."}
          </p>
        </div>
      )}

      {isAdmin && (
        <Card className="border border-slate-200 rounded-xl bg-white overflow-hidden" data-testid="month-closures-card">
          <div className="px-5 py-4 border-b border-slate-100">
            <h2 className="text-base font-semibold text-slate-900">Cierres mensuales de inventario</h2>
            <p className="text-xs text-slate-500 mt-1">La liquidacion y la apertura se aprueban y recalculan como un solo grupo por mercado.</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="month-closures-table">
              <thead className="bg-slate-50 text-slate-600">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold">Periodo</th>
                  <th className="px-4 py-3 text-left font-semibold">Mercado</th>
                  <th className="px-4 py-3 text-right font-semibold">Saldo a trasladar</th>
                  <th className="px-4 py-3 text-right font-semibold">Costo promedio</th>
                  <th className="px-4 py-3 text-left font-semibold">Estado</th>
                  <th className="px-4 py-3 text-right font-semibold">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {closures.length === 0 && <tr><td colSpan={6} className="text-center py-8 text-slate-400">No hay cierres pendientes</td></tr>}
                {closures.map((closure) => (
                  <tr key={closure.id} className="border-t border-slate-100" data-testid={`closure-row-${closure.id}`}>
                    <td className="px-4 py-3 font-mono">{closure.close_year}-{String(closure.close_month).padStart(2, "0")}</td>
                    <td className="px-4 py-3 capitalize">{closure.market}</td>
                    <td className="px-4 py-3 text-right font-mono">{fmt.usdt(closure.balance_usdt)} USDT</td>
                    <td className="px-4 py-3 text-right font-mono">{closure.market === "detalle" ? `${fmt.dop(closure.cost_unit)} DOP` : `${fmt.usdt(closure.cost_unit)} USD`}</td>
                    <td className="px-4 py-3"><Badge className={STATUS_BADGE[closure.status] || "bg-slate-100 text-slate-600"}>{closure.status}</Badge></td>
                    <td className="px-4 py-3 text-right">
                      {(closure.status === "pendiente" || closure.status === "bloqueada") && <Button size="sm" onClick={() => { setReviewingClosure(closure); setComment(""); }} data-testid={`review-closure-${closure.id}`} className="bg-slate-900">Revisar grupo</Button>}
                      {closure.status === "rechazada" && <Button size="sm" variant="outline" onClick={() => regenerateClosure(closure)}><RefreshCw size={14} className="mr-1" /> Recalcular</Button>}
                      {(closure.status === "aprobada" || closure.status === "no_requerido") && (
                        <div className="inline-flex items-center gap-2">
                          <span className="text-xs text-slate-500">Revision {closure.revision || 1}</span>
                          <Button size="sm" variant="outline" onClick={() => regenerateClosure(closure)} data-testid={`regenerate-closure-${closure.id}`}>
                            <RefreshCw size={14} className="mr-1" /> Recalcular
                          </Button>
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Card className="border border-slate-200 rounded-xl bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="corrections-table">
            <thead className="bg-slate-50 text-slate-600">
              <tr>
                <th className="px-4 py-3 text-left font-semibold">Fecha</th>
                <th className="px-4 py-3 text-left font-semibold">Operador</th>
                <th className="px-4 py-3 text-left font-semibold">Tipo</th>
                <th className="px-4 py-3 text-left font-semibold">Propuesta</th>
                <th className="px-4 py-3 text-left font-semibold">Motivo</th>
                <th className="px-4 py-3 text-left font-semibold">Estado</th>
                <th className="px-4 py-3 text-right font-semibold">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {list.length === 0 && (<tr><td colSpan={7} className="text-center py-8 text-slate-400">Sin solicitudes</td></tr>)}
              {list.map((c) => (
                <tr key={c.id} className="border-t border-slate-100" data-testid={`corr-row-${c.id}`}>
                  <td className="px-4 py-3 text-slate-600 font-mono tabular-nums text-xs">{fmt.date(c.created_at)}</td>
                  <td className="px-4 py-3 font-medium text-slate-800">{c.requested_by_name}</td>
                  <td className="px-4 py-3"><Badge variant="outline">{TYPE_LABEL[c.request_type] || c.request_type}</Badge></td>
                  <td className="px-4 py-3 text-xs text-slate-600 max-w-xs truncate" title={renderProposed(c)}>{renderProposed(c)}</td>
                  <td className="px-4 py-3 text-slate-600 max-w-xs">{c.reason}</td>
                  <td className="px-4 py-3"><Badge className={STATUS_BADGE[c.status]}>{c.status}</Badge></td>
                  <td className="px-4 py-3 text-right">
                    {isAdmin && c.status === "pendiente" && (
                      <Button size="sm" onClick={() => setReviewing(c)} data-testid={`review-${c.id}`} className="bg-slate-900">Revisar</Button>
                    )}
                    {c.admin_comment && c.status !== "pendiente" && (
                      <span className="text-xs text-slate-500">"{c.admin_comment}"</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={!!reviewingClosure} onOpenChange={(open) => !open && setReviewingClosure(null)}>
        <DialogContent data-testid="closure-review-dialog" className="max-w-lg">
          <DialogHeader><DialogTitle>Aprobar cierre mensual</DialogTitle></DialogHeader>
          {reviewingClosure && (
            <div className="space-y-4 text-sm">
              <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                <div className="flex justify-between"><span className="text-slate-500">Periodo</span><strong>{reviewingClosure.close_year}-{String(reviewingClosure.close_month).padStart(2, "0")}</strong></div>
                <div className="flex justify-between"><span className="text-slate-500">Mercado</span><strong className="capitalize">{reviewingClosure.market}</strong></div>
                <div className="flex justify-between"><span className="text-slate-500">Venta de cierre</span><strong>{fmt.usdt(reviewingClosure.balance_usdt)} USDT</strong></div>
                <div className="flex justify-between"><span className="text-slate-500">Compra de apertura</span><strong>{fmt.usdt(reviewingClosure.balance_usdt)} USDT</strong></div>
                <div className="flex justify-between"><span className="text-slate-500">Costo trasladado</span><strong>{reviewingClosure.market === "detalle" ? `${fmt.dop(reviewingClosure.cost_unit)} DOP` : `${fmt.usdt(reviewingClosure.cost_unit)} USD`}</strong></div>
              </div>
              <p className="text-xs text-slate-600">Al aprobar, ambas transacciones se aplicaran juntas. Las correcciones retroactivas futuras generaran una nueva revision del grupo.</p>
              {reviewingClosure.status === "bloqueada" && <p className="text-xs text-rose-700">Este cierre esta bloqueado porque el saldo calculado es negativo. Revisa las operaciones antes de aprobar.</p>}
              <div>
                <Label>Comentario administrador (opcional)</Label>
                <Textarea value={comment} onChange={(e) => setComment(e.target.value)} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => reviewClosure("reject")} className="border-rose-200 text-rose-700">Rechazar</Button>
            <Button onClick={() => reviewClosure("approve")} disabled={reviewingClosure?.status === "bloqueada"} className="bg-emerald-600 hover:bg-emerald-700" data-testid="approve-closure-btn"><Check size={16} className="mr-1" /> Aprobar grupo</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!reviewing} onOpenChange={(o) => !o && setReviewing(null)}>
        <DialogContent data-testid="review-dialog" className="max-w-lg">
          <DialogHeader><DialogTitle>Revisar solicitud</DialogTitle></DialogHeader>
          {reviewing && (
            <div className="space-y-3 text-sm">
              <div className="bg-slate-50 rounded-lg p-3">
                <div><span className="font-semibold">Operador:</span> {reviewing.requested_by_name}</div>
                <div><span className="font-semibold">Tipo:</span> {TYPE_LABEL[reviewing.request_type]}</div>
                <div><span className="font-semibold">Motivo:</span> {reviewing.reason}</div>
                <div className="mt-2"><span className="font-semibold">Propuesta:</span> <span className="font-mono text-xs">{renderProposed(reviewing)}</span></div>
              </div>
              <div>
                <Label>Comentario administrador (opcional)</Label>
                <Textarea value={comment} onChange={(e) => setComment(e.target.value)} data-testid="review-comment" />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => review("reject")} className="border-rose-200 text-rose-700 hover:bg-rose-50" data-testid="reject-btn"><X size={16} className="mr-1" /> Rechazar</Button>
            <Button onClick={() => review("approve")} className="bg-emerald-600 hover:bg-emerald-700" data-testid="approve-btn"><Check size={16} className="mr-1" /> Aprobar y aplicar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
